/* eslint-disable import/prefer-default-export */

import mirror from 'keymirror';
import UserFactory from '../lib/user-factory';
import Cookie from '../lib/cookie';
import segment from '../core/segment';
import Subscriptions from '../lib/subscriptions';
import { parseProfile, parseZipCode, parseZipResponse } from '../utils/fender-connect-utils';

export const actionTypes = mirror({
  FC_ACCOUNT_SUMMARY: null,
  FC_AUTH_RESPONSE: null,
  FC_CREATE_ACCOUNT_ERROR: null,
  FC_FORGOT_PASSWORD_ERROR: null,
  FC_FORGOT_PASSWORD_STATUS_RESET: null,
  FC_FORGOT_PASSWORD_STATUS_SUCCESS: null,
  FC_GET_USER_ERROR: null,
  FC_GET_USER_SUCCESS: null,
  FC_SIGNIN_ERROR: null,
  FC_LOGIN_SHOW: null,
  FC_LOGIN_REDIRECT: null,
  FC_LOGIN_HIDE: null,
  FC_PAYMENT_METHOD_ERROR: null,
  FC_REQUEST_IN_PROGRESS: null,
  FC_RESET_ERROR: null,
  FC_SESSION_CHECK_COMPLETE: null,
  FC_SIGNOUT: null,
  FC_SUBSCRIBE: null,
  FC_UPDATE_PROFILE: null,
  FC_UPDATE_PROFILE_ERROR: null,
  FC_UPDATE_PROFILE_COMPLETE: null,
  FC_REDEEM_CODE_ERROR: null,
  FC_RESET_PASSWORD_ERROR: null,
  FC_VALIDATE_CODE_ERROR: null,
  FC_VALIDATE_CODE_IN_PROGRESS: null,
  FC_VALIDATE_CODE_SUCCESS: null,
  FC_STREAKS_TESTING_CREATED_AT: null,
});

function requestInProgress() {
  return {
    type: actionTypes.FC_REQUEST_IN_PROGRESS,
  };
}

function validateInProgress() {
  return {
    type: actionTypes.FC_VALIDATE_CODE_IN_PROGRESS,
  };
}

function sessionCheckComplete() {
  return {
    type: actionTypes.FC_SESSION_CHECK_COMPLETE,
  };
}

function signInError(error) {
  return {
    type: actionTypes.FC_SIGNIN_ERROR,
    error,
  };
}

function createAccountError(error) {
  return {
    type: actionTypes.FC_CREATE_ACCOUNT_ERROR,
    error,
  };
}

function forgotPasswordError(error) {
  return {
    type: actionTypes.FC_FORGOT_PASSWORD_ERROR,
    error,
  };
}

function authResponse(user) {
  return {
    type: actionTypes.FC_AUTH_RESPONSE,
    user,
  };
}

function forgotPasswordSuccessStatus() {
  return {
    type: actionTypes.FC_FORGOT_PASSWORD_STATUS_SUCCESS,
  };
}

export function showLogin() {
  return {
    type: actionTypes.FC_LOGIN_SHOW,
  };
}

export function hideLogin() {
  return {
    type: actionTypes.FC_LOGIN_HIDE,
  };
}

function subscribe() {
  return {
    type: actionTypes.FC_SUBSCRIBE,
  };
}

function paymentError(error) {
  return {
    type: actionTypes.FC_PAYMENT_METHOD_ERROR,
    error,
  };
}

function redeemCodeError(error) {
  return {
    type: actionTypes.FC_REDEEM_CODE_ERROR,
    error,
  };
}

function validateCodeError(error) {
  return {
    type: actionTypes.FC_VALIDATE_CODE_ERROR,
    error,
  };
}

function validateCodeSuccess(response) {
  return {
    type: actionTypes.FC_VALIDATE_CODE_SUCCESS,
    response,
  };
}

function updateProfileError(error) {
  return {
    type: actionTypes.FC_UPDATE_PROFILE_ERROR,
    error,
  };
}

function updateProfileComplete(userProfile) {
  return {
    type: actionTypes.FC_UPDATE_PROFILE_COMPLETE,
    userProfile,
  };
}

function receiveAccountSummary(accountSummary) {
  return {
    type: actionTypes.FC_ACCOUNT_SUMMARY,
    accountSummary,
  };
}

function getUserError(error) {
  return {
    type: actionTypes.FC_GET_USER_ERROR,
    error,
  };
}

function getUserSuccess(response) {
  return {
    type: actionTypes.FC_GET_USER_SUCCESS,
    response,
  };
}

function resetPasswordError(error) {
  return {
    type: actionTypes.FC_RESET_PASSWORD_ERROR,
    error,
  };
}

function setStreaksCreatedAt(createdAt) {
  return {
    type: actionTypes.FC_STREAKS_TESTING_CREATED_AT,
    createdAt,
  };
}

export function updateProfile(profileID, attributes) {
  return async dispatch => {
    dispatch(requestInProgress());
    try {
      const response = await UserFactory.updateProfile(profileID, attributes);
      dispatch(updateProfileComplete(response));
    } catch (error) {
      dispatch(updateProfileError(error));
    }
  };
}

export function validateCode(promoCode) {
  return async dispatch => {
    dispatch(validateInProgress());
    const response = await Subscriptions.validate(promoCode);
    if (response.status === 200 || response.status === 201 || response.status === 204) {
      const payload = await response.json();
      dispatch(validateCodeSuccess(payload));
    } else {
      dispatch(validateCodeError(response));
    }
  };
}

export function redeemPromoCode(promoCode, zipCode, countryCode) {
  return async (dispatch, getState) => {
    dispatch(requestInProgress());
    const { fenderConnect } = getState();
    const zip = parseZipCode(zipCode, countryCode);
    const zipResponse = await Subscriptions.getCityStateZippopotamus(zip, countryCode);
    if (!zipResponse.places || !zipResponse.places.length) {
      segment.event('Zip Code Validation Error', { countryCode, zipCode, fenderConnect });
    }
    const parsedZip = parseZipResponse(zipResponse);
    const parsedProfile = parseProfile(promoCode, fenderConnect);
    const attributes = { postal: zipCode, ...parsedZip, ...parsedProfile };
    const response = await Subscriptions.redeem(attributes);
    if (response.status >= 200 && response.status < 300) {
      dispatch(subscribe());
    } else {
      dispatch(redeemCodeError(response));
    }
  };
}

export function getAccountSummary(userID) {
  return async dispatch => {
    const response = await Subscriptions.getAccountSummary(userID);
    if (response) {
      dispatch(receiveAccountSummary(response));
    }
  };
}

export function signIn(email, password) {
  return async dispatch => {
    dispatch(requestInProgress());
    try {
      const user = await UserFactory.authenticate(email, password);
      dispatch(authResponse(user));
    } catch (error) {
      dispatch(signInError(error));
    }
  };
}

export function createAccount(email, password, firstName, lastName, touAccepted, source) {
  return async dispatch => {
    dispatch(requestInProgress());
    try {
      const user = await UserFactory.create(
        email,
        password,
        firstName,
        lastName,
        source,
        touAccepted,
      );
      dispatch(authResponse(user));
    } catch (error) {
      dispatch(createAccountError(error));
    }
  };
}

export function forgotPassword(email, source) {
  return async dispatch => {
    dispatch(requestInProgress());
    try {
      await UserFactory.forgotPassword(email, source);
      dispatch(forgotPasswordSuccessStatus());
    } catch (error) {
      dispatch(forgotPasswordError(error));
    }
  };
}

export function resetForgotPasswordStatus() {
  return {
    type: actionTypes.FC_FORGOT_PASSWORD_STATUS_RESET,
  };
}

export function signOut() {
  Cookie.clear();
  return {
    type: actionTypes.FC_SIGNOUT,
  };
}

export function tokenError(response) {
  return async dispatch => {
    let errorMsg = `An error has occurred. \n
                    Please try again or contact customer support 1-844-202-0924.`;
    if (response.status === 402) {
      errorMsg = `Sorry, the transaction was declined by your card issuer. \n
                  Please try a different card.`;
    }
    dispatch(paymentError(errorMsg));
  };
}

export function resetError() {
  return {
    type: actionTypes.FC_RESET_ERROR,
  };
}

export function checkForSession() {
  return async dispatch => {
    try {
      const user = await UserFactory.checkForExistingSession();
      if (user) {
        dispatch(authResponse(user));
        dispatch(sessionCheckComplete());
      } else if (!user) {
        dispatch(sessionCheckComplete());
      }
    } catch (error) {
      Cookie.clear();
      dispatch(signOut());
    }
  };
}

export function getUser(token) {
  return async dispatch => {
    try {
      const user = await UserFactory.getUser(token);
      if (user) {
        dispatch(getUserSuccess(user));
      } else {
        dispatch(getUserError());
      }
    } catch (error) {
      dispatch(getUserError(error));
    }
  };
}

export function resetPassword(userID, password, email, token) {
  return async dispatch => {
    dispatch(requestInProgress());
    try {
      await UserFactory.resetPassword(userID, password, token);
      dispatch(signIn(email, password));
    } catch (error) {
      dispatch(resetPasswordError(error));
    }
  };
}
/**
 * This action is only used for manipulating
 * streaks data for testing purposes. It has no
 * use inside of a production environment.
 */
export function setCreatedAt(date) {
  return async dispatch => {
    dispatch(setStreaksCreatedAt(date));
  };
}
